import { h } from 'preact'
import Header from '../header'
import { PanelHeaderWithIcon } from '../ui-components/PanelHeaderWithIcon'
import axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'
import CreateEmailForm from './CreateEmailForm'
import CreateEmailPreview from './CreateEmailPreview'
import { UsernameVariableNode } from '../ui-components/EditableInputWithVariable'
import AccordionPanel from '../panel/AccordionPanel'
import DropdownControl from '../dropdown/DropdownControl'
import ButtonControl from '../button-control'
import Icon from '../assets'
import ListSelector from '../list-selector/ListSelector'
import SendEmailCampaignConfirmationModal from './SendEmailCampaignConfirmationModal'
import { Message } from '../ui-components/Message'
import SendTestEmailModal from './SendTestEmailModal'
// @ts-ignore
import contentCss from '!!css-loader!sass-loader!../../../../sass/src/modules/_post_content.scss'
import StateTracker from '../state-tracker'
import StatelessPanel from '../panel/StatelessPanel'
import tracker from '../../components/tracker/publish'
import RadioInputGroup from '../ui-components/RadioInputGroup'
import classNames from 'classnames'

type EmailCampaign = {
  emailCampaignId: number | null,
  title: string,
  subject: string,
  content: string,
  selectedRole: CustomRoleWithCount | null
}

type CustomRoleWithCount = {
  id: number,
  role: string,
  users: string
}

type Stats = {
  scheduledMonthly: number,
  monthlyLimit: number
}

type Props = {
  emailCampaignId?: number,
  title?: string,
  subject?: string,
  content?: string,
  fromEmail?: string,
  fromUsername?: string,
  fromId?: number,
  status?: 'Draft' | 'Sent',
  customRoles?: number[],
  templateContent?: string | null,
  showDefaultChildren?: boolean
}

type State = {
  emailCampaign: EmailCampaign,
  alertMessage?: string,
  alertMessageType?: string,
  subjectError: string,
  showCreateEmailEdit: boolean,
  showSendTestEmailModal: boolean,
  fromEmail: string,
  fromUsername: string,
  fromId: number,
  status: string,
  customRoles?: number[] | null,
  allCustomRoles: CustomRoleWithCount[],
  showSendConfirmationModal: boolean,
  hasChanged: boolean,
  templateContent: string | null,
  selectedRecipientsOption: string,
  allUsers: number | null,
  stats: Stats | null
}

class VariableParser {
  _result: string;

  constructor (value: string) {
    this._result = value
  }

  toInputVariable () {
    this._result = this._result.replace(/{username}/g, UsernameVariableNode)
    return this
  }

  toBracketVariable () {
    this._result = this._result
      .replace(/<input-variable.+?>/g, '{')
      .replace(/<\/input-variable+?>/g, '}')
    return this
  }

  removeBreakTags () {
    this._result = this._result.replace(/<br>/g, '')
    return this
  }

  replaceBlankSpaces () {
    this._result = this._result.replace(/&nbsp;/g, ' ')
    return this
  }

  result () {
    return this._result
  }
}

const addEditorStyles = (content: string): string => {
  const styles = `<style>${contentCss}</style>`
  const postContent = `<div class="post__content post__content--new-editor post__content--email">${content}</div>`
  return `${styles}${postContent}`
}

const removeEditorStyles = (content: string): string => {
  let tmp = document.createElement('span')
  tmp.innerHTML = content
  const postContentDiv = tmp.querySelector('.post__content--email')
  return postContentDiv ? postContentDiv.innerHTML : content
}

const CHOOSE_RECIPIENTS_PANEL_INDEX = 0
const SEND_EMAIL_PANEL_INDEX = 1

const CUSTOM_ROLES_OPTION = 'Users with a certain user role'

class EmailCampaignCreate extends StateTracker<Props, State, 'emailCampaign'> {
  initialState: { emailCampaign: EmailCampaign }
  private accordionPanel: AccordionPanel | null = null
  private webmasterName: string = ''
  private webmasterEmail: string = ''
  private allUsersOption: string = ''

  constructor (props: Props) {
    super()
    this.state = {
      emailCampaign: {
        emailCampaignId: props.emailCampaignId || null,
        title: props.title || '',
        subject: props.subject ? new VariableParser(props.subject).toInputVariable().result() : '',
        content: props.content ? removeEditorStyles(new VariableParser(props.content).toInputVariable().result()) : '<p>Hi,</p>',
        selectedRole: null
      },
      subjectError: '',
      showCreateEmailEdit: false,
      fromEmail: props.fromEmail || window.inSidedData.user.email as string,
      fromUsername: props.fromUsername || window.inSidedData.user.name,
      fromId: props.fromId || parseInt(window.inSidedData.user.userid),
      status: props.status || 'Draft',
      customRoles: props.customRoles || null,
      allCustomRoles: [],
      showSendConfirmationModal: false,
      showSendTestEmailModal: false,
      hasChanged: false,
      templateContent: props.templateContent || null,
      selectedRecipientsOption: 'All users in community',
      allUsers: null,
      stats: null
    }

    this.initialState = {
      emailCampaign: this.state.emailCampaign
    }

    this.onTitleChanged = this.onTitleChanged.bind(this)
    this.onSubjectChanged = this.onSubjectChanged.bind(this)
    this.onContentChanged = this.onContentChanged.bind(this)
    this.onAction = this.onAction.bind(this)
    this.hideAlertMessage = this.hideAlertMessage.bind(this)
    this.onPreviewClicked = this.onPreviewClicked.bind(this)
    this.onEmailCreateDone = this.onEmailCreateDone.bind(this)
    this.onRoleSelected = this.onRoleSelected.bind(this)
    this.onRecipientsRadioChanged = this.onRecipientsRadioChanged.bind(this)
  }

  componentWillUnmount () {
    super.componentWillUnmount()
  }

  async componentDidMount () {
    let webmaster = await this.fetchWebmaster()
    this.webmasterName = webmaster.webmasterName
    this.webmasterEmail = webmaster.webmasterEmail

    this.setState({
      allCustomRoles: await this.fetchCustomerRoles()
    }, () => {
      const { emailCampaign, customRoles, allCustomRoles } = this.state
      if (customRoles) {
        this.setState({
          emailCampaign: { ...emailCampaign, ...{ selectedRole: allCustomRoles.find((customRoleWithCount: CustomRoleWithCount) => customRoleWithCount.id === customRoles[0]) || null } }
        })
      }
    })

    await this.setEmailTemplate()
    await this.setVisibleUsersCount()
    await this.setStats()

    super.componentDidMount()
    this.setInitialState({ emailCampaign: this.state.emailCampaign })
  }

  onPropChanged = (prop: string, value: string | CustomRoleWithCount | null) => {
    const { emailCampaign } = this.state
    this.trackStateChange({
      emailCampaign: { ...emailCampaign, ...{ [prop]: value } }
    })
  }

  onTitleChanged (title: string) {
    this.onPropChanged('title', title)
  }

  onSubjectChanged (value: string) {
    this.onPropChanged('subject', value)
  }

  onContentChanged (content: string) {
    this.onPropChanged('content', content)
  }

  onRecipientsRadioChanged (option: string) {
    this.setState({ selectedRecipientsOption: option })

    if (option === this.allUsersOption) {
      this.onPropChanged('selectedRole', null)
    }
  }

  onPreviewClicked () {
    this.setState({ showCreateEmailEdit: true }, () => {
      const activePanelIndex = this.accordionPanel?.state.activePanelIndex
      if (typeof activePanelIndex !== 'undefined') this.accordionPanel?.togglePanel(activePanelIndex)
    })
  }

  onEmailCreateDone () {
    this.setState({ showCreateEmailEdit: false }, () => {
      if (!this.state.emailCampaign.selectedRole) {
        this.accordionPanel?.togglePanel(CHOOSE_RECIPIENTS_PANEL_INDEX)
      } else {
        this.accordionPanel?.togglePanel(SEND_EMAIL_PANEL_INDEX)
      }
    })
  }

  hideAlertMessage () {
    this.setState({
      alertMessageType: undefined,
      alertMessage: ''
    })
  }

  async fetchCustomerRoles () {
    const res = await axios.get('/role/fetchCustomRolesWithCount')
    return res.data
  }

  async fetchWebmaster () {
    const res = await axios.get('/settings/emailManagement/getWebmaster')
    return res.data
  }

  async setEmailTemplate () {
    if (this.state.templateContent) return
    try {
      const res = await axios.get('/emailTemplate/find/1')
      this.setState({ templateContent: res.data.Content })
    } catch {
      this.setState({ alertMessageType: 'error', alertMessage: 'Something went wrong, please try again' })
    }
  }

  async setVisibleUsersCount () {
    const res = await axios.get('/role/getVisibleUsersCount')
    this.allUsersOption = `All users in community (${res.data})`

    this.setState({
      selectedRecipientsOption: this.props.customRoles ? CUSTOM_ROLES_OPTION : this.allUsersOption,
      allUsers: res.data
    })
  }

  setStats = async () => {
    const res = await axios.get('/emailCampaign/stats')
    this.setState({
      stats: res.data
    })
  }

  getMonthlyUsage = (): number => {
    const { emailCampaign: { selectedRole }, allUsers, stats } = this.state

    if(!allUsers || !stats) {
     return 0
    }

    return selectedRole ? stats.scheduledMonthly + Number(selectedRole.users) : stats.scheduledMonthly + allUsers
  }

  hasReachedLimit = (): boolean => {
    const { stats } = this.state
    if(!stats) return false
    return this.getMonthlyUsage() > stats.monthlyLimit
  }

  onRoleSelected (role: CustomRoleWithCount) {
    this.onPropChanged('selectedRole', role)
  }

  getSanitizedSubject = (subject: string) => new VariableParser(subject)
    .toBracketVariable()
    .removeBreakTags()
    .replaceBlankSpaces()
    .result()

  trackEvent = (action: string) => {
    const { fromId, fromUsername } = this.state
    const { emailCampaignId, title, subject, selectedRole } = this.state.emailCampaign

    let payload = {
      email_campaign_id: emailCampaignId,
      email_campaign_name: title || this.getSanitizedSubject(subject),
      email_campaign_creator_id: fromId,
      email_campaign_creator_username: fromUsername
    }

    if (action === 'save') {
      payload = { ...payload, ...{ type: 'Email Campaign Saved' } }
    }

    if (action === 'send') {
      payload = { ...payload,
        ...{
          type: 'Email Campaign Sent',
          email_campaign_recipient_count: selectedRole?.users
        } }
    }

    if (action === 'sendTestEmail') {
      payload = { ...payload, ...{ type: 'Email Campaign Test Email Sent' } }
    }

    tracker.trackEvent('userevent', payload)
  }

  async onAction (action: 'save' | 'send' | 'sendTestEmail', redirectToOverview: boolean = false) {
    const { emailCampaign: { emailCampaignId, title, subject, content, selectedRole }, fromEmail, fromUsername, fromId } = this.state

    const sanitizedSubject = this.getSanitizedSubject(subject)

    const sanitizedContent = new VariableParser(content)
      .toBracketVariable()
      .result()

    if (sanitizedSubject === '') {
      this.setState({ showCreateEmailEdit: true })
      if (action === 'sendTestEmail') {
        this.setState({
          alertMessageType: 'error',
          alertMessage: 'Please enter email subject'
        })
      } else {
        this.setState({
          subjectError: 'Please enter a subject'
        })
      }

      if (this.accordionPanel && this.accordionPanel.state.activePanelIndex !== CHOOSE_RECIPIENTS_PANEL_INDEX) {
        this.accordionPanel?.togglePanel(CHOOSE_RECIPIENTS_PANEL_INDEX)
      }

      return
    } else {
      this.setState({ subjectError: '' })
    }

    try {
      await axios.post(`/emailCampaign/${action === 'sendTestEmail' ? 'send' : action}`, {
        emailCampaignId: emailCampaignId || null,
        title: title || sanitizedSubject,
        subject: sanitizedSubject,
        content: addEditorStyles(sanitizedContent),
        senderEmail: fromEmail,
        senderUsername: fromUsername,
        senderId: fromId,
        customRoleIds: selectedRole ? [selectedRole.id] : null,
        isTestEmail: action === 'sendTestEmail'
      }).then((res) => {
        const { emailCampaignId } = res.data

        this.setState({
          emailCampaign: { ...this.state.emailCampaign,
            ...{
              emailCampaignId: emailCampaignId,
              title: title || sanitizedSubject
            } }
        }, () => this.trackEvent(action))

        if (action === 'sendTestEmail') {
          this.setState({
            showSendTestEmailModal: true
          })
        } else {
          this.setState({
            alertMessageType: 'success',
            alertMessage: `Email campaign '${this.state.emailCampaign.title}' has been ${action === 'save' ? 'saved' : 'sent'} successfully!`
          })
        }

        this.setInitialState({ emailCampaign: this.state.emailCampaign })

        redirectToOverview ? window.location.assign('/email/email-campaigns') : window.history.pushState(null, '', `/email/email-campaign/${emailCampaignId}`)
      })
    } catch (e) {
      this.setState({
        alertMessageType: 'error'
      })
    }
  }

  render (_ : Props, { emailCampaign: { title, subject, content, selectedRole }, alertMessage, alertMessageType, subjectError, showCreateEmailEdit, fromEmail, fromUsername, status, allCustomRoles, showSendConfirmationModal, showSendTestEmailModal, templateContent, selectedRecipientsOption, allUsers, stats }: State) {
    const ChooseRecipientsPanel = () => ({
      onClick: () => this.setState({ showCreateEmailEdit: false }),
      title: <PanelHeaderWithIcon
        customClass='panel-header-container'
        iconCustomClass='icon-panel-header'
        icon='usersPlus'
        title='Choose recipients'
        isDone={!!selectedRole || selectedRecipientsOption === this.allUsersOption}
        meta={selectedRole ? `${selectedRole.users} users | ${selectedRole.role}` : selectedRecipientsOption === this.allUsersOption ? `${allUsers} users | All users in community` : ''}
      />,
      body: <div className='panel-body-container'>
        <RadioInputGroup
          options={[this.allUsersOption, CUSTOM_ROLES_OPTION]}
          onChange={this.onRecipientsRadioChanged}
          value={selectedRecipientsOption}
          underneath
        />
        <DropdownControl
          align='adjust'
          triggerClass='select-user-role-dropdown'
          closeOnDropdownClick
          showDefaultChildren
          trigger={
            <ButtonControl
              title={<div style={{ display: 'flex', alignItems: 'center' }}>
                {selectedRole ? selectedRole.role : 'Select a user role'}
                <Icon icon='chevronDown' width={16} height={16} />
              </div>}
              newStyle
              size='medium'
              disabled={selectedRecipientsOption === this.allUsersOption}
            />
          }
          items={[]}
          disabled={selectedRecipientsOption === this.allUsersOption}
          newStyle>
          {({ close } : { close: () => void }) =>
            <ListSelector
              hasSearch
              placeholder='Search for custom roles'
              items={(allCustomRoles || []).filter((role: CustomRoleWithCount) => Number(role.users) > 0).map((role: CustomRoleWithCount) => ({
                title: role.role,
                ...role
              }))}
            >{(roles: CustomRoleWithCount[]) =>
              <div className='list'>
                  {roles.map((role: CustomRoleWithCount) =>
                  <div className='list-item' onClick={() => {
                      this.onRoleSelected(role)
                      close()
                    }}>
                      {role.role} ({role.users})
                    </div>
                  )}
                </div>
              }
            </ListSelector>}
        </DropdownControl>
        <br />
        <div>
          <p>After sending the current campaign, here's what your usage will look like:</p>
          <p>Emails sent this month: <span class={classNames({ 'text-red': this.hasReachedLimit() })}>{ this.getMonthlyUsage()}</span>/{ stats?.monthlyLimit} </p>
          { stats && this.hasReachedLimit() && <Message text='Sorry, there isn’t enough email usage left, please choose fewer recipients or send this email later.' variant='danger'/> }
        </div>
        <ButtonControl title='Done' newStyle size='medium' onClick={() => this.accordionPanel?.togglePanel(SEND_EMAIL_PANEL_INDEX)} variant='brand' />
      </div>,
      customClass: 'qa-choose-recipients-panel'
    })

    const SendEmailPanel = () => {
      const isCustomRoleSelected = selectedRecipientsOption === CUSTOM_ROLES_OPTION
      const isAllUsersSelected = selectedRecipientsOption === this.allUsersOption
      const isReadyToSend = (isCustomRoleSelected && (!!selectedRole && !!subject) || (isAllUsersSelected && !!subject)) && !this.hasReachedLimit()
      return {
        onClick: () => this.setState({ showCreateEmailEdit: false }),
        title: <PanelHeaderWithIcon
          customClass='panel-header-container'
          iconCustomClass='icon-panel-header'
          icon='send'
          title='Send email'
        />,
        body: <div className='panel-body-container'>
          {isReadyToSend && <Message text='All set! You are good to go.' variant='success' />}
          {!isReadyToSend && <Message variant='warning'>
            <div>
              {!subject && <div>Please enter an email subject (<span className='underline pointer'
                onClick={() => {
                  this.setState({ showCreateEmailEdit: true })
                  this.accordionPanel?.togglePanel(SEND_EMAIL_PANEL_INDEX)
                }}>Open step</span>)
              </div>}
              {isCustomRoleSelected && !selectedRole && <div>Please choose at least one recipient for your message (<span className='underline pointer' onClick={() => {
                this.accordionPanel?.togglePanel(CHOOSE_RECIPIENTS_PANEL_INDEX)
              }}>Open step</span>)
              </div>}
              {this.hasReachedLimit() && <div>There isn’t enough email usage left, please choose less recipients or send this email later (<span className='underline pointer' onClick={() => {
                this.accordionPanel?.togglePanel(CHOOSE_RECIPIENTS_PANEL_INDEX)
              }}>Open step</span>)
              </div>}
            </div>
          </Message>}
          <div>
            <ButtonControl title='Send email now' newStyle size='medium' onClick={() => this.setState({ showSendConfirmationModal: true })} variant='brand'
              disabled={!isReadyToSend} />
            <ButtonControl title='Save and close' newStyle size='medium' onClick={() => this.onAction('save', true)} />
          </div>
        </div>,
        customClass: 'qa-send-email-panel'
      }
    }

    return <div className='create-email-campaign'>
      <Header
        title={title}
        disableCancelButton
        disableSaveButton
        onTitleChanged={this.onTitleChanged}
        placeholder='Untitled Email Campaign'
        status={status}
        customButtons={[
          <ButtonControl
            title='Send test email'
            size='medium'
            class='qa-send-test-email'
            onClick={() => this.onAction('sendTestEmail')}
            newStyle
          />,
          <ButtonControl
            title='Save changes'
            size='medium'
            onClick={() => this.onAction('save')}
            variant='success'
            newStyle
          />
        ]}
        statusVariant={status === 'Draft' ? 'draft' : 'published'}
      />
      {alertMessageType && <AlertComponent
        messageType={alertMessageType}
        message={alertMessage}
        sticky
        isVisibleFor={5000}
        onTimeoutEnd={this.hideAlertMessage}
        onClose={this.hideAlertMessage}
      />}
      <StatelessPanel
        newStyle
        title={<PanelHeaderWithIcon
          customClass='panel-header-container'
          iconCustomClass='icon-panel-header'
          icon='editor'
          title='Create email'
          isDone={!!(subject && content)}
        />}
      >
        {showCreateEmailEdit
          ? <CreateEmailForm
            subject={subject}
            onSubjectChanged={this.onSubjectChanged}
            subjectError={subjectError}
            content={content}
            onContentChanged={this.onContentChanged}
            onEmailCreateDone={this.onEmailCreateDone} />
          : <CreateEmailPreview
            subject={subject}
            content={content}
            fromUsername={this.webmasterName}
            fromEmail={this.webmasterEmail}
            onPreviewClicked={this.onPreviewClicked}
            template={templateContent} />
        }
      </StatelessPanel>
      <AccordionPanel
        ref={ref => this.accordionPanel = ref}
        panels={[
          ChooseRecipientsPanel(),
          SendEmailPanel()
        ]}
      />
      { showSendConfirmationModal && <SendEmailCampaignConfirmationModal
        isOpen={showSendConfirmationModal}
        numberOfUsers={selectedRole ? Number(selectedRole.users) : allUsers ? allUsers : 0}
        onSend={() => this.onAction('send', true)}
        onClose={() => this.setState({ showSendConfirmationModal: false })}
      /> }
      { showSendTestEmailModal && <SendTestEmailModal
        showModal={showSendTestEmailModal}
        onClose={() => this.setState({ showSendTestEmailModal: false })}
      /> }
    </div>
  }
}

export default EmailCampaignCreate
